import type { PageHelp } from '../network-help.data';

export const L10N_ru_g4: Record<string, PageHelp> = {
  'trains-ticket': {
    title: 'Железнодорожный билет',
    tag: 'Оформленный электронный билет',
    tagline: 'Оформленный электронный ж/д билет — номер бронирования, вагон / место, QR и прибыль от продажи.',
    business: {
      lead: 'Это подтверждение железнодорожной продажи — номер бронирования и QR / штрихкод, который пассажир сканирует при посадке, а также подтверждённые вагон и место. Он также замыкает коммерческий цикл, показывая агенту реализованную маржу по бронированию.',
      points: [
        { title: 'Посадка по QR', desc: 'Номер и штрихкод — это то, что пассажир предъявляет при посадке.' },
        { title: 'Вагон и место подтверждены', desc: 'Точный вагон и место (и его расположение / статус) указаны в билете.' },
        { title: 'Прибыль реализована', desc: 'Финансовая сводка делает маржу по этой продаже явной.' },
      ],
    },
    software: {
      lead: 'Билет с маршрутом, вагоном / местом и финансами.',
      points: [
        { title: 'Шапка и полоса пассажира', desc: 'Номер бронирования, имя пассажира, номер билета, класс и дата оформления.' },
        { title: 'Маршрут и место', desc: 'Перевозчик, номер рейса, станции отправления / прибытия и длительность, а также ряд вагон / место и штрихкод.' },
        { title: 'Финансы и действия', desc: 'Боковая панель маржи, а также элементы управления email / копирование / изменение / возврат.' },
      ],
    },
    related: ['trains-payment', 'bookings', 'booking-detail', 'refunds'],
  },

  // ── Hotels funnel (search → results [book in drawer] → voucher) ──
  'hotels-results': {
    title: 'Результаты по отелям',
    tag: 'Поиск отелей',
    tagline: 'Объекты размещения для проживания, ранжированные — с видимым режимом продажи и политикой возврата до подтверждения.',
    business: {
      lead: 'Экран результатов показывает каждый доступный для бронирования объект по запросу, объединяя собственные контрактные ресурсы с подключённым предложением. Коммерчески важные бейджи — Мгновенное vs По запросу, возвратное vs невозвратное — находятся на карточке, поэтому агент выбирает, зная, как подтвердится бронирование и когда движутся деньги.',
      points: [
        { title: 'Собственное + подключённое предложение объединены', desc: 'Номера из модуля «Объект размещения» платформы и от внешних поставщиков ранжируются в одном списке.' },
        { title: 'Режим продажи виден', desc: 'Мгновенное подтверждается по клику; По запросу удерживает бронирование, пока поставщик не примет — бейдж говорит агенту, что он получает.' },
        { title: 'Политика возврата заранее', desc: 'Бесплатная отмена vs невозвратное — это фильтр и тег, поэтому агент называет правильную политику.' },
      ],
    },
    software: {
      lead: 'Фильтруемый список объектов с полосой тарифов и панелью выбора номера.',
      points: [
        { title: 'Фильтрация объектов', desc: 'Боковая панель сужает по сортировке, звёздности, диапазону цен, политике возврата, району и поставщику, плюс свободный текст по названию / району / сети.' },
        { title: 'Просмотр полосы тарифов', desc: 'Календарь цен за ночь на 7 дней вокруг даты заезда; клик по дате запускает повторный поиск.' },
        { title: 'Выбор номера для бронирования', desc: 'Карточки показывают лучшую цену за ночь vs стандартную, оценку отзывов и тег возврата; «Выбрать номер» открывает панель бронирования.' },
      ],
    },
    flow: {
      lead: 'Со страницы результатов бронирование завершается в панели (а не на отдельных страницах), которая ведёт к ваучеру.',
      steps: [
        { title: 'Выбор номера и тарифа', desc: 'Выберите тип номера и тарифный план; итог покрывает все ночи плюс налоги, с показанной политикой возврата.' },
        { title: 'Данные гостя', desc: 'Одна форма на каждого гостя на каждый номер (обращение, имя, дата рождения, гражданство, паспорт); сохранённый клиент подставляется из CRM. Основной гость несёт контакт по бронированию.' },
        { title: 'Оплата (+ дополнения)', desc: 'Выберите способ оплаты; для собственных ресурсов здесь можно добавить опциональные допуслуги (парковка, спа…).' },
        { title: 'Проверка и подтверждение', desc: 'Финальная сводка по номерам, гостям и итогу; подтверждение создаёт заказ и выпускает ваучер.' },
      ],
    },
    related: ['hotels-search', 'hotels-ticket', 'supply-properties', 'markup'],
  },

  'hotels-ticket': {
    title: 'Ваучер отеля',
    tag: 'Выпущенный ваучер',
    tagline: 'Подтверждённый ваучер отеля — объект, даты проживания, список гостей и начисления.',
    business: {
      lead: 'Ваучер — это подтверждение подтверждённого проживания для гостя и документ, который отель принимает при заселении. Он объединяет объект, тарифный план и питание, даты заезда / выезда и документы каждого гостя в одну запись, которую агентство может отправить по email или распечатать.',
      points: [
        { title: 'Подтверждение для заселения', desc: 'Номер плюс список гостей по номерам (с документами) — это то, что отель проверяет по прибытии.' },
        { title: 'Питание и возврат в записи', desc: 'Питание тарифного плана и политика отмены указаны в ваучере, а не спрятаны в договоре.' },
        { title: 'Обслуживание отсюда', desc: 'Отправьте маршрут по email, скопируйте номер или измените / отмените бронирование.' },
      ],
    },
    software: {
      lead: 'Ваучер с объектом, полосой проживания, списком гостей по номерам и начислениями.',
      points: [
        { title: 'Шапка и объект', desc: 'Бейдж «ваучер выпущен» и номер, объект с тегами Подтверждено / питание / возврат, а также email / печать / скачивание.' },
        { title: 'Полоса проживания', desc: 'Дата заезда (с 15:00), количество ночей и выезд (до 12:00).' },
        { title: 'Гости и начисления', desc: 'Список гостей по номерам с документами, плюс боковая панель начислений (база номера, налоги, итог).' },
      ],
    },
    related: ['hotels-results', 'bookings', 'booking-detail'],
  },

  // ── Tours — package authoring (the sell flow lives on the `tours` page via drawers) ──
  'tours-new': {
    title: 'Конструктор турпакета',
    tag: 'Создание пакета',
    tagline: 'Соберите мультисервисный пакет — маршрут, авиаперелёты, отели, включённые услуги и цены по размещению — затем опубликуйте его для продажи.',
    business: {
      lead: 'Это сторона предложения в Турах: где оператор собирает продукт, который агенты позже продают. Пакет объединяет авиаперелёты, отели, трансферы и активности в один SKU с собственным маршрутом, окном вылета и пакетной ценой — чтобы его можно было продвигать по общей цене, а маржу управлять как единым целым, а не отстаивать построчно.',
      points: [
        { title: 'Один SKU, много компонентов', desc: 'Авиаперелёты и отель всегда включены; виза, трансферы и страхование — опциональные переключатели — всё продаётся как единая запись.' },
        { title: 'Цена по размещению', desc: 'Каждая корзина отеля тарифицируется по ролям размещения (Взрослый, Взрослый-одноместное, Ребёнок-с-местом, Ребёнок-без-места, Младенец), чтобы семья или одиночный путешественник получили правильную цену.' },
        { title: 'Статус публикации управляет продажей', desc: 'Пакет проходит Неактивен → Предпросмотр → Активен; только Активные вылеты (не прошедшие) можно бронировать со страницы Туров.' },
      ],
    },
    software: {
      lead: 'Пошаговый мастер от основ до публикации, с валидацией каждого шага перед продвижением.',
      points: [
        { title: 'Основы, маршрут, компоненты', desc: 'Название, города, даты, категория и бейджи; план по дням с городом ночёвки на каждый день; и список включено / исключено из каталога или свободный текст.' },
        { title: 'Авиаперелёты и корзины отелей', desc: 'Прикрепите перелёт (поиск-и-бронирование в режиме тура или вручную) и соберите мультигородские корзины отелей (город + отель + номер).' },
        { title: 'Ценообразование и публикация', desc: 'Матрица цен по-корзинно × по-размещению, затем публикация с флагами дистрибуции (избранное, еженедельная рассылка, ограничение рынка, B2C white-label).' },
      ],
    },
    related: ['tours', 'flights-search', 'hotels-search', 'supply-properties'],
  },

  visa: {
    title: 'Виза',
    tag: 'Визовые услуги',
    tagline: 'Бронируемые визовые заявки с каталогом, проверкой права и рабочим местом офицера за стойкой.',
    business: {
      lead: 'Виза превращает оформление разрешения на въезд в бронируемую услугу. Агентство продаёт визу так же, как продаёт авиаперелёт или отель — как продукт с ценой, сроком и определённой поставкой — с бэк-офисным процессом офицера за кулисами, который фактически собирает документы, подаёт в консульство и возвращает решение.',
      points: [
        { title: 'Виза — это продукт', desc: 'Каждая пара направление × тип визы имеет запись в каталоге с ценой, сроком и требованиями к документам; наценка агентства накладывается сверху.' },
        { title: 'Право на въезд ограничивает продажу', desc: 'Гражданство путешественника, цель поездки и окно действия определяют, какие записи каталога действительно продаваемы — поиск предлагает только допустимые сочетания.' },
        { title: 'Деньги захвачены, услуга поставлена', desc: 'Оплата берётся при бронировании; рабочее место офицера отслеживает прогресс от «документы получены» до «решение возвращено».' },
      ],
    },
    software: {
      lead: 'Поиск по каталогу виз плюс бэк-офисное рабочее место офицера для стороны исполнения.',
      points: [
        { title: 'Поиск по каталогу', desc: 'Направление, тип визы и гражданство путешественника определяют результаты; появляются только продаваемые сочетания.' },
        { title: 'Бронирование визы', desc: 'Выбор записи открывает форму путешественника / загрузки документов и оплату; результат — визовое бронирование.' },
        { title: 'Отслеживание до поставки', desc: 'Рабочее место офицера показывает каждое открытое дело, его этап, документы и статус на стороне консульства — агент использует это для обновления клиента.' },
      ],
    },
    flow: {
      lead: 'Визовое бронирование состоит из двух половин — продажа для клиента, затем бэк-офисное исполнение вплоть до решения.',
      steps: [
        { title: 'Фильтрация каталога', desc: 'Страна назначения, тип визы (туристическая / деловая / транзитная / студенческая) и гражданство путешественника определяют право; недопустимые сочетания скрыты.' },
        { title: 'Чтение записи', desc: 'Каждая запись каталога показывает сбор, срок, требования к документам и канал решения консульства.' },
        { title: 'Регистрация путешественника', desc: 'Данные путешественника (паспорт, даты поездки, цель, подтверждающие сведения) формируют дело.' },
        { title: 'Загрузка документов', desc: 'Скан паспорта, фото, приглашение, финансовое подтверждение — необходимый набор варьируется по записи каталога.' },
        { title: 'Оплата и открытие дела', desc: 'Оплата захватывается при бронировании. Дело открывается в рабочем месте офицера на этапе «документы получены».' },
        { title: 'Исполнение офицером', desc: 'Офицер проверяет документы, подаёт в консульство, отслеживает статус, получает решение и закрывает дело визовым документом или отказом.' },
        { title: 'Поставка клиенту', desc: 'Визовый документ (или объяснение отказа) прикрепляется к бронированию и делается доступным агенту для передачи.' },
      ],
    },
    modes: {
      lead: 'Записи виз каталогизированы по типу визы; процесс адаптируется под каждый тип.',
      modes: [
        { name: 'Tourist', when: 'путешествие ради отдыха', desc: 'Стандартная гостевая виза. Ниже сбор, быстрее срок на большинстве направлений. Набор документов обычно паспорт + фото + маршрут.' },
        { name: 'Business', when: 'рабочие / конференц-визиты', desc: 'Требует письмо-приглашение от принимающей организации. Выше сбор, схожий срок, иногда личная запись в консульство.' },
        { name: 'Transit', when: 'пересадка в стране назначения', desc: 'Кратчайший срок действия, самый дешёвый сбор. Действует только на транзитное окно. Не все направления предлагают её; поиск фильтрует их соответственно.' },
        { name: 'Student', when: 'официальное зачисление в местное учебное заведение', desc: 'Требует подтверждение зачисления и часто финансовую гарантию. Самый долгий срок; самый высокий сбор. Рабочее место офицера отслеживает расширенный документарный цикл.' },
      ],
    },
    examples: [
      {
        title: 'Туристическая виза в Турцию · 1 пасс. · срок 21 день',
        rows: [
          { label: 'Консульский сбор', value: '4,500,000' },
          { label: 'Сервисный сбор (обработка агентством)', value: '1,500,000' },
          { label: 'Розничный MARKUP · +10% от брутто', value: '+600,000', accent: 'success' },
          { label: 'Итог для путешественника', value: '6,600,000', strong: true },
        ],
        note: 'Консульский сбор проходит транзитом; агентство зарабатывает на сервисном сборе и розничной наценке, а не на консульской стоимости.',
      },
    ],
    related: ['flights-search', 'reference-data', 'markup'],
  },

  cip: {
    title: 'CIP',
    tag: 'Аэропортовый консьерж',
    tagline: 'Услуги встречи-и-сопровождения и VIP-консьержа в аэропорту, продаваемые вместе с поездкой.',
    business: {
      lead: 'CIP (Commercially Important Person) — это аэропортовый консьерж — услуга встречи-и-сопровождения, которая соединяет клиента с сотрудником аэропорта на прилёте, транзите или вылете. Коммерчески это небольшое, высокомаржинальное дополнение, которое агентство может прикрепить к любой подходящей поездке, и в основном продаётся агентствам-реселлерам, а не напрямую путешественникам.',
      points: [
        { title: 'Дополнительный доход', desc: 'Бронирование консьержа дёшево в исполнении, высокомаржинально и не конкурирует с основной продажей перелёта/отеля — чистый дополнительный доход.' },
        { title: 'Только для реселлеров', desc: 'Каталог CIP открыт агентствам (не клиентам) — это оптовый продукт, который реселлер перепродаёт своему конечному клиенту.' },
        { title: 'Принадлежит cip-service', desc: 'Данные каталога CIP принадлежат выделенному cip-service, а не content-service, поэтому цена и доступность управляются независимо от остальных справочных данных.' },
      ],
    },
    software: {
      lead: 'Выделенный экран поиска и бронирования по каталогу консьержа на базе cip-service.',
      points: [
        { title: 'Поиск по каталогу', desc: 'Аэропорт, тип услуги (прилёт / вылет / транзит) и количество путешественников определяют результаты.' },
        { title: 'Бронирование услуги', desc: 'Выбор услуги открывает форму путешественника и процесс оплаты; результат — бронирование CIP, которое появляется рядом с любым другим продуктом во «Всех бронированиях».' },
        { title: 'Цена от и до', desc: 'Деньги захватываются при бронировании; сверка проводок с учётным стеком — на дорожной карте Фазы-3.' },
      ],
    },
    flow: {
      lead: 'Бронирование CIP короткое — путешественник выбирает аэропорт, момент (прилёт / вылет / транзит) и уровень услуги.',
      steps: [
        { title: 'Выбор аэропорта', desc: 'Код аэропорта определяет, какая сеть сотрудников доступна; не в каждом аэропорту есть покрытие.' },
        { title: 'Выбор момента', desc: 'Прилёт, вылет или транзит — сотрудник начинает и заканчивает в разных точках внутри терминала для каждого.' },
        { title: 'Выбор уровня услуги', desc: 'Встреча-и-сопровождение, VIP или полный доступ в лаундж — каждый уровень включает больше привилегий (fast-track, электрокар, время в лаундже).' },
        { title: 'Указание данных рейса', desc: 'Номер и дата рейса, чтобы сотрудник мог встретить нужного путешественника.' },
        { title: 'Регистрация путешественников + оплата', desc: 'Имена путешественников + оплата; сотрудник получает назначение, а клиент получает подтверждение с местом встречи.' },
      ],
    },
    modes: {
      lead: 'CIP покупается в один из трёх моментов поездки и на одном из нескольких уровней услуги — каталог является их матрицей.',
      modes: [
        { name: 'Arrival', when: 'прибывающий рейс', desc: 'Сотрудник встречает путешественника у гейта / телетрапа, помогает с иммиграцией и багажом, сопровождает в город. Популярно у VIP и впервые прибывающих.' },
        { name: 'Departure', when: 'вылетающий рейс', desc: 'Сотрудник встречает путешественника со стороны города, помогает с регистрацией и досмотром, сопровождает к гейту или в лаундж.' },
        { name: 'Transit', when: 'стыковочный рейс', desc: 'Сотрудник встречает между рейсами, обеспечивает переход между терминалами и гейтами. Особенно ценно на тесных стыковках.' },
        { name: 'Meet & Assist', when: 'базовый уровень', desc: 'Сотрудник и физическая помощь — основная услуга.' },
        { name: 'VIP', when: 'премиум-уровень', desc: 'Добавляет fast-track через иммиграцию и досмотр, транспорт на электрокаре внутри терминала, иногда доступ в лаундж.' },
      ],
    },
    examples: [
      {
        title: 'DXB прилёт · VIP · 2 пасс.',
        rows: [
          { label: 'Базовый сбор сотрудника (за пасс.) × 2', value: '2,800,000' },
          { label: 'Wholesale MARKUP · +8% (агентство сотрудника)', value: '+224,000', accent: 'info' },
          { label: 'Розничный MARKUP · +15% от брутто', value: '+453,600', accent: 'success' },
          { label: 'Итог для путешественника', value: '3,477,600', strong: true },
        ],
        note: 'CIP — высокомаржинальное дополнение — смешанная наценка 20–30% здесь нормальна, против 5–10% на перелётах.',
      },
    ],
    related: ['flights-search', 'reference-data', 'markup'],
  },

  ground: {
    title: 'Наземный транспорт',
    tag: 'Наземные услуги',
    tagline: 'Трансферы от точки до точки, услуги с водителем и краткие автоперевозки.',
    business: {
      lead: 'Наземный транспорт продаёт дорожный этап поездки — аэропортовые трансферы, встречи с водителем, аренду авто на день — как бронируемый продукт. Это спутник «добраться из А в Б по земле» к продаже перелёта или отеля, и он позволяет агентству закрыть всю поездку клиента в одной корзине.',
      points: [
        { title: 'Замыкает поездку', desc: 'У клиента, который уже забронировал перелёт и отель через агентство, есть очевидный следующий вопрос — как добраться от одного до другого. Наземный транспорт отвечает на него внутри компании.' },
        { title: 'Привязывается к перелёту', desc: 'Аэропортовые трансферы обычно покупаются после выбора перелёта; время подачи может наследоваться от прибытия рейса, снижая усилия оператора.' },
        { title: 'Применяется слоевое ценообразование', desc: 'Тот же движок Wholesale / Distribution / Retail, который тарифицирует перелёт, тарифицирует и наземную услугу — правило с партнёрским скоупом работает и здесь.' },
      ],
    },
    software: {
      lead: 'Поиск подача / высадка с опциями класса авто и процессом бронирования.',
      points: [
        { title: 'Составление этапа', desc: 'Место подачи, место высадки, дата и количество пассажиров определяют поиск; класс авто (седан / внедорожник / вэн) сужает выбор.' },
        { title: 'Чтение строки авто', desc: 'Каждый результат показывает оператора, класс авто, цену и оценочную длительность.' },
        { title: 'Переход к бронированию', desc: 'Выбранное авто открывает форму пассажира / деталей подачи и оплату; запись присоединяется ко «Всем бронированиям».' },
      ],
    },
    flow: {
      lead: 'Наземное бронирование короткое и часто привязывается к существующему перелёту — место подачи, высадка, время, класс авто.',
      steps: [
        { title: 'Выбор подачи + высадки', desc: 'Город-в-город, аэропорт-в-отель или точка-в-точку. Подача из аэропорта предзаполняется из сопоставленного рейса в том же PNR.' },
        { title: 'Выбор даты + времени', desc: 'Отметка времени подачи — для аэропортовых трансферов она может наследоваться от прибытия рейса (с поправкой на стандартный буфер).' },
        { title: 'Выбор класса авто', desc: 'Седан для одного / пары, внедорожник для семьи, вэн для группы. Класс авто определяет вместимость, багаж и цену.' },
        { title: 'Регистрация пассажиров + оплата', desc: 'Имя и телефон основного пассажира + оплата; водитель отправляется, а путешественник получает детали подачи.' },
      ],
    },
    modes: {
      lead: 'Наземные услуги делятся по форме поездки — разовый трансфер или продолжающаяся аренда — и по классу авто.',
      modes: [
        { name: 'Аэропортовый трансфер', when: 'прибывающий / вылетающий рейс', desc: 'Разовая подача из аэропорта в отель (или наоборот). Самый дешёвый и распространённый. Время подачи наследуется от прибытия рейса.' },
        { name: 'Точка-в-точку', when: 'город → город или межгородской переезд', desc: 'Одна высадка в другом месте города или региона. Цена масштабируется с расстоянием.' },
        { name: 'С водителем / почасово', when: 'деловые встречи / VIP-день', desc: 'Водитель + авто на забронированный блок часов. Путешественник диктует маршрут дня.' },
        { name: 'Аренда на день', when: 'осмотр достопримечательностей на весь день', desc: 'Авто + водитель на весь день, обычно с максимальным лимитом километража. Цена выше почасового блока.' },
      ],
    },
    examples: [
      {
        title: 'Аэропорт DXB → отель · внедорожник · 2 пасс. + багаж',
        rows: [
          { label: 'Базовый тариф трансфера', value: '600,000' },
          { label: 'Wholesale MARKUP · +5% (маржа оператора)', value: '+30,000', accent: 'info' },
          { label: 'Розничный MARKUP · +10% от брутто', value: '+63,000', accent: 'success' },
          { label: 'Итог для путешественника', value: '693,000', strong: true },
        ],
        note: 'Наземная услуга, привязанная к существующему PNR перелёта, разделяет запись бронирования — одна оплата, один ваучер, один процесс возврата.',
      },
    ],
    related: ['flights-search', 'buses-search', 'markup'],
  },

  sim: {
    title: 'SIM-карты',
    tag: 'Связь в поездке',
    tagline: 'Тревел-SIM / eSIM только для данных, продаваемые как платное дополнение к любой зарубежной поездке.',
    business: {
      lead: 'SIM-карты продают тариф данных для направления вместе с самой поездкой. Клиент избегает роуминговых сборов, агентство зарабатывает небольшую маржу, а исполнение (отправка физической SIM или мгновенная выдача QR eSIM) достаточно лёгкое, чтобы себестоимость оставалась низкой.',
      points: [
        { title: 'Чистое дополнение', desc: 'Маржа за единицу мала, но стоимость привязки ничтожна; это дополнительный доход на каждой зарубежной поездке на отдых.' },
        { title: 'Физическая + eSIM', desc: 'Физическим SIM нужен адрес доставки и срок; eSIM выдаются как QR-код сразу при бронировании, что меняет процесс.' },
        { title: 'Привязка к направлению', desc: 'Каждый тариф привязан к стране или региону и объёму данных / окну действия — каталог является матрицей этих сочетаний.' },
      ],
    },
    software: {
      lead: 'Поиск направление + тариф на базе каталога SIM.',
      points: [
        { title: 'Поиск по каталогу', desc: 'Страна назначения и окно действия определяют результаты; объём данных сужает далее.' },
        { title: 'Чтение строки тарифа', desc: 'Каждая строка показывает оператора, объём данных, срок действия и цену; тип SIM (физическая vs eSIM) влияет на исполнение.' },
        { title: 'Бронирование тарифа', desc: 'Выбор тарифа открывает форму путешественника / деталей доставки и оплату; записи eSIM выдаются мгновенно, физические SIM планируют доставку.' },
      ],
    },
    flow: {
      lead: 'Бронирование SIM — один из самых коротких процессов инвентаря — направление, объём, тип SIM, выдача.',
      steps: [
        { title: 'Выбор направления', desc: 'Страна или регион — каталог индексируется по зоне покрытия, а не по оператору.' },
        { title: 'Выбор окна действия', desc: 'Длительность поездки определяет, какие тарифы подходят — тариф на 7 дней не подойдёт для поездки на 21 день.' },
        { title: 'Выбор объёма', desc: 'Бюджет данных (безлимит / 10GB / 20GB) сужает далее; безлимитные тарифы обычно ограничивают скорость после лимита честного использования.' },
        { title: 'Выбор типа SIM', desc: 'Физическая SIM (нужен адрес доставки + срок) или eSIM (QR выдаётся при бронировании). Совместимость телефона путешественника определяет выбор.' },
        { title: 'Регистрация + оплата', desc: 'Путешественник + адрес доставки (только физическая) + оплата. QR eSIM показывается сразу на подтверждении; отправка физической SIM планируется.' },
      ],
    },
    modes: {
      lead: 'Важны два измерения — исполнение физическая vs eSIM и географическое покрытие.',
      modes: [
        { name: 'Физическая SIM', when: 'телефон путешественника не поддерживает eSIM', desc: 'Nano / micro SIM, отправляемая на адрес путешественника (или получаемая на стойке партнёра). Нужен срок 3–5 дней на большинстве маршрутов, поэтому бронируйте заранее.' },
        { name: 'eSIM', when: 'телефон путешественника поддерживает eSIM (большинство современных смартфонов)', desc: 'QR-код выдаётся при бронировании — без отправки, без срока. Путешественник сканирует по прибытии, и профиль активируется. Нулевая себестоимость для агентства.' },
        { name: 'По стране', when: 'поездка в одно направление', desc: 'Тариф действует в одной стране. Самая дешёвая ставка за ГБ.' },
        { name: 'Региональный', when: 'многострановая поездка (напр. по Европе)', desc: 'Тариф действует в пределах определённого региона (по всему ЕС, Азия-Тихоокеанский регион, ССАГПЗ). Выше ставка за ГБ, но не нужно менять SIM на каждой границе.' },
        { name: 'Глобальный', when: 'долгая мультирегиональная поездка', desc: 'Тариф действует в широком списке стран — 100+ стран. Самая высокая ставка за ГБ; подходит для кругосветных или деловых путешественников.' },
      ],
    },
    examples: [
      {
        title: 'Турция · 10 дней 10GB · eSIM · 1 пасс.',
        rows: [
          { label: 'Стоимость тарифа поставщика', value: '600,000' },
          { label: 'Розничный MARKUP · +30% (высокомаржинальное дополнение)', value: '+180,000', accent: 'success' },
          { label: 'Итог для путешественника', value: '780,000', strong: true },
        ],
        note: 'У eSIM нулевая стоимость исполнения — QR генерируется поставщиком и выдаётся при подтверждении, поэтому вся наценка уходит в маржу.',
      },
      {
        title: 'Та же поездка · физическая SIM',
        rows: [
          { label: 'Стоимость тарифа поставщика', value: '600,000' },
          { label: 'Доставка', value: '100,000' },
          { label: 'Розничный MARKUP · +25% от брутто', value: '+175,000', accent: 'success' },
          { label: 'Итог для путешественника', value: '875,000', strong: true },
        ],
        note: 'Физические SIM добавляют стоимость и срок доставки — версия eSIM обычно продаётся лучше, когда телефон клиента её поддерживает.',
      },
    ],
    related: ['visa', 'insurance', 'flights-search'],
  },

  insurance: {
    title: 'Страхование путешествий',
    tag: 'Защита поездки',
    tagline: 'Бронируемые полисы страхования путешествий, продаваемые как дополнение к поездке.',
    business: {
      lead: 'Страхование путешествий защищает клиента от нарушений поездки (задержки, отмены, медицина, багаж) и даёт агентству дополнительную статью дохода. Некоторые направления требуют действующего покрытия как условие визы, поэтому оно также может быть продуктом соответствия, а не чисто опциональным.',
      points: [
        { title: 'Существуют и опциональное, и обязательное', desc: 'Для некоторых виз покрытие требуется для получения; для чисто отдыхающих поездок это опция, которую агент допродаёт при закрытии.' },
        { title: 'Уровневые планы', desc: 'План определяется своими лимитами покрытия и длительностью — клиент выбирает уровень, соответствующий его толерантности к риску и бюджету.' },
        { title: 'Андеррайтинг внешний', desc: 'Платформа не андеррайтит полис — она продаёт сторонний продукт и бронирует получившуюся запись полиса на поездке клиента.' },
      ],
    },
    software: {
      lead: 'Поиск путешественник + направление по подключённому каталогу страхования.',
      points: [
        { title: 'Составление покрытия', desc: 'Направление, даты поездки и возраст путешественников определяют результаты; уровень покрытия сужает план.' },
        { title: 'Чтение строки плана', desc: 'Каждый результат показывает страховщика, лимиты покрытия и цену; документы полиса открываются при выборе.' },
        { title: 'Бронирование полиса', desc: 'Выбор плана открывает форму путешественника и оплату; запись полиса и его документы прикрепляются к поездке клиента.' },
      ],
    },
    flow: {
      lead: 'Бронирование полиса короткое — направление, даты поездки, возраст путешественников, уровень, оплата.',
      steps: [
        { title: 'Выбор формы поездки', desc: 'Страна / регион назначения, даты поездки и возраст каждого путешественника (ставка масштабируется по возрастной группе).' },
        { title: 'Выбор уровня покрытия', desc: 'Basic покрывает только основное; Standard добавляет багаж / задержку; Premium добавляет полное медицинское + отмену поездки. Выбирайте под стоимость поездки под риском.' },
        { title: 'Чтение плана', desc: 'Каждый план перечисляет лимиты покрытия по строкам (медицина, отмена, багаж) и исключения. Документы полиса открываются на том же экране.' },
        { title: 'Регистрация путешественников + оплата', desc: 'Имена, даты рождения и номера паспортов путешественников + оплата; полис выпускается при бронировании.' },
        { title: 'Получение документов полиса', desc: 'PDF полиса + сертификат прикрепляются к бронированию. Некоторые направления требуют сертификат при подаче в консульство.' },
      ],
    },
    modes: {
      lead: 'Страхование тарифицируется по двум осям — уровень покрытия (что покрыто) и форма поездки (разовая vs годовая).',
      modes: [
        { name: 'Уровень Basic', when: 'внутренние / короткие поездки на отдых', desc: 'Медицинская экстренность + репатриация. Багаж и отмена не включены. Самая дешёвая премия.' },
        { name: 'Уровень Standard', when: 'большинство поездок на отдых', desc: 'Добавляет потерю багажа, задержку поездки и ограниченную отмену. Самый продаваемый уровень.' },
        { name: 'Уровень Premium', when: 'дорогие поездки или путешественники старшего возраста', desc: 'Добавляет полную медицину (выше лимиты), полную отмену-по-любой-причине, приключенческие активности. Самая высокая премия.' },
        { name: 'Разовая поездка', when: 'одна поездка в ближайшие недели', desc: 'Покрывает конкретное окно дат (начало → конец поездки). Самая дешёвая для нечастых путешественников.' },
        { name: 'Годовой мультитрип', when: 'частые деловые или отдыхающие поездки', desc: 'Покрывает каждую поездку в течение года, до лимита длительности на поездку (обычно 30–60 дней). Дешевле при 3+ поездках в год, чем покупать разовую каждый раз.' },
      ],
    },
    examples: [
      {
        title: 'Турция · поездка 10 дней · уровень Standard · 2 взрослых + 1 ребёнок',
        rows: [
          { label: 'Базовая премия (взрослый) × 2', value: '1,400,000' },
          { label: 'Базовая премия (ребёнок)', value: '400,000' },
          { label: 'Подытог', value: '1,800,000' },
          { label: 'Розничный MARKUP · +20% комиссия', value: '+360,000', accent: 'success' },
          { label: 'Итог для путешественника', value: '2,160,000', strong: true },
        ],
        note: 'Комиссии страхования обычно 15–25% от премии — солидная дополнительная маржа на бронирование, и один полис покрывает всех в поездке.',
      },
    ],
    related: ['visa', 'sim', 'flights-search'],
  },

  // ── RESERVATIONS section ────────────────────────────────────────────────

  bookings: {
    title: 'Все бронирования',
    tag: 'Книга заказов',
    tagline: 'Каждый заказ, сделанный агентством, по всем продуктам, в одном списке.',
    business: {
      lead: 'Все бронирования — это операционная запись всего проданного — авиаперелёты, отели, туры, транспорт, визы, CIP — как унифицированные заказы. Здесь сотрудники находят бронирование клиента для его обслуживания: переоформить, добавить экстра, отменить, вернуть или просто проверить, где оно стоит.',
      points: [
        { title: 'Один заказ, много продуктов', desc: 'Единый заказ может объединять перелёт, отель и дополнение — каждый является позицией под одним номером / PNR.' },
        { title: 'Статус говорит, что делать', desc: 'Черновик → Удержано → Подтверждено → Оплачено, плюс На проверке (проживание По запросу в ожидании подтверждения) и Отменено — чип показывает, где стоит бронирование.' },
        { title: 'Поверхность обслуживания', desc: 'Каждое послепродажное действие (отмена, возврат, начисления, документы) начинается с поиска заказа здесь.' },
      ],
    },
    software: {
      lead: 'Искомая, фильтруемая таблица заказов — и цель глобального поиска в верхней панели.',
      points: [
        { title: 'Быстрый поиск', desc: 'Поиск по номеру, PNR, пассажиру или контакту; фильтр по продукту, статусу или дате.' },
        { title: 'Глобальный поиск ведёт сюда', desc: 'Поиск в верхней панели, не давший уникального попадания, перечисляет совпадения здесь; уникальное попадание сразу переходит к деталям бронирования.' },
        { title: 'Открыть для обслуживания', desc: 'Кликните строку, чтобы открыть полные детали бронирования и действовать по нему.' },
      ],
    },
    related: ['booking-detail', 'queue', 'refunds', 'counter'],
  },

  'booking-detail': {
    title: 'Детали бронирования',
    tag: 'Запись заказа',
    tagline: 'Полная пятислойная запись одного заказа — позиции, деньги, платежи, документы и действия.',
    business: {
      lead: 'Бронирование моделируется в пяти слоях — Заказ → Позиция → Ссылка поставщика → Документ → Купон — так что каждый тариф, налог, сбор и возврат относятся точно к тому, чему принадлежат. Этот экран — где один заказ понимается и обслуживается от и до.',
      points: [
        { title: 'Пять слоёв, точная атрибуция', desc: 'Деньги суммируются от купонов и документов к позициям, а затем к заказу, так что вы всегда знаете, кто что должен.' },
        { title: 'Отмена на каждом уровне', desc: 'Отмените один купон, документ, позицию или весь заказ — всегда с расчётом перед любым движением денег.' },
        { title: 'По запросу живёт здесь', desc: 'Ожидающий (На проверке) запрос на отель показывает панель Подтвердить / Отклонить — Подтвердить захватывает авторизованный платёж, Отклонить его аннулирует.' },
      ],
    },
    software: {
      lead: 'Запись с вкладками, с финансами по каждому уровню и послепродажными действиями.',
      points: [
        { title: 'Позиции', desc: 'Постатейная разбивка — каждый продукт как позиция с его сегментами / номерами, пассажирами и статусом.' },
        { title: 'Финансы', desc: 'Денежная матрица: нетто, наценки, налоги, сборы, скидки, комиссии и маржа на каждом уровне.' },
        { title: 'Платежи', desc: 'Платёжные плечи (авторизовано / захвачено / возвращено / аннулировано) и остаток к оплате.' },
        { title: 'Документы', desc: 'Выпущенные документы поставщика; электронный авиабилет открывает готовый к печати двуязычный ваучер авиабилета, и можно прикрепить сканы агентства.' },
        { title: 'Начисления', desc: 'Послебронировочные начисления — добавьте сборы, допуслуги, штрафы или скидки, и структурные изменения (добавить пассажира / позицию, коррекция имени) с VAT.' },
        { title: 'Тикеты', desc: 'Тикеты поддержки, поднятые по позициям этого бронирования между агентством и стойкой платформы.' },
        { title: 'Отмена и возврат', desc: 'Рассчитайте и выполните отмену на любом уровне; возвраты направляются в процесс согласования «Возвраты и аннулирования».' },
      ],
    },
    examples: [
      {
        title: 'Как суммируются деньги (одна позиция отеля)',
        rows: [
          { label: 'Нетто поставщика', value: '7,400,000' },
          { label: '+ Розничная наценка', value: '+1,110,000', accent: 'info' },
          { label: '+ Допуслуги и VAT', value: '+990,000', accent: 'success' },
          { label: 'Итог продажи', value: '9,500,000', strong: true, accent: 'accent' },
          { label: '− Оплачено', value: '9,500,000' },
          { label: 'Остаток к оплате', value: '0', strong: true },
        ],
        note: 'Итог заказа — это сумма его позиций; итог продажи каждой позиции — это её нетто плюс слои наценки, начисления и налоги, отнесённые к ней.',
      },
    ],
    related: ['bookings', 'refunds', 'queue'],
  },

  queue: {
    title: 'Очередь бронирований',
    tag: 'Удержания PNR',
    tagline: 'PNR, удерживающие инвентарь, которым ещё нужно оформление или действие поставщика.',
    business: {
      lead: 'Удержанный PNR резервирует инвентарь, но ещё не оформлен — это обещание на срок. Очередь выводит их, чтобы сотрудники действовали до истечения удержания и авто-возврата места или номера поставщику.',
      points: [
        { title: 'Удержания скоропортящиеся', desc: 'Неоформленные PNR авто-отменяются при истечении окна удержания (свипер запускается каждые 60 секунд), освобождая инвентарь.' },
        { title: 'Успеть до срока', desc: 'Время-до-оформления — это метрика, которая важна — оформите вовремя, или бронирование потеряно.' },
        { title: 'Не только оформление', desc: 'Некоторые строки ждут поставщика (напр. подтверждение По запросу), а не только вашего действия по оформлению.' },
      ],
    },
    software: {
      lead: 'Упорядоченный по времени рабочий список удержаний с их сроками.',
      points: [
        { title: 'Видеть, что истекает', desc: 'Каждая строка показывает истечение удержания, поэтому самые срочные всплывают наверх.' },
        { title: 'Действовать по строке', desc: 'Откройте бронирование, чтобы выпустить / оформить или предпринять требуемое действие поставщика.' },
        { title: 'Авто-отмена автоматична', desc: 'Истёкшие удержания освобождаются фоновым свипером и выпадают из очереди.' },
      ],
    },
    related: ['bookings', 'booking-detail'],
  },

  refunds: {
    title: 'Возвраты и аннулирования',
    tag: 'Послепродажные деньги',
    tagline: 'Контролируемый процесс возврата денег — отмены, возвраты и аннулирования.',
    business: {
      lead: 'Когда бронирование отменяется, деньги должны вернуться правильно и под контролем. «Возвраты и аннулирования» — это где запросы поднимаются и согласуются, чтобы возвраты следовали правилам тарифа и ничто не уходило без проверки.',
      points: [
        { title: 'Инициировать vs согласовать', desc: 'Поднятие возврата и его согласование — отдельные шаги — согласование является воротами движения денег, за собственным разрешением.' },
        { title: 'Аннулирование vs возврат', desc: 'Аннулирование отменяет авторизацию, которая никогда не была захвачена (ничего не списано); возврат возвращает захваченные деньги за вычетом любого штрафа.' },
        { title: 'Правила, не догадки', desc: 'Суммы следуют правилам тарифа / отмены и любому штрафу; запрос фиксирует разбивку.' },
      ],
    },
    software: {
      lead: 'Очередь запросов возврата / аннулирования с их статусом и суммами.',
      points: [
        { title: 'Работа с очередью', desc: 'Видьте ожидающие запросы с их суммой, штрафом и причиной.' },
        { title: 'Согласовать или отклонить', desc: 'Согласуйте, чтобы двинуть деньги (вернуть захват / аннулировать авторизацию), или отклоните с причиной.' },
        { title: 'Прослеживаемо', desc: 'Каждое решение атрибутировано и связано с исходным бронированием.' },
      ],
    },
    related: ['booking-detail', 'bookings'],
  },

  counter: {
    title: 'Касса',
    tag: 'Наличные и POS',
    tagline: 'Смены кассира, движения наличных и POS, и сверка на конец дня.',
    business: {
      lead: 'Для агентств, продающих через физическую стойку, это слой контроля наличных: он отслеживает, кто на смене, каждое движение наличных и карты, и сводит кассу в конце дня, чтобы выручка совпадала с записью.',
      points: [
        { title: 'Ответственность за смену', desc: 'Открытие и закрытие смены привязывает каждое движение к кассиру и сессии.' },
        { title: 'Кассовая дисциплина', desc: 'Запись движений наличных и POS по мере их совершения — это то, что делает сверку конца дня возможной.' },
        { title: 'Сверять, не гадать', desc: 'Закрытие дня сравнивает ожидаемое с подсчитанным, чтобы расхождение всплывало немедленно.' },
      ],
    },
    software: {
      lead: 'Дашборд текущей смены с её движениями и сверкой.',
      points: [
        { title: 'Открыть / закрыть смену', desc: 'Начните сессию, чтобы начать запись; закройте её, чтобы свести.' },
        { title: 'Запись движений', desc: 'Логируйте приход / расход наличных и POS-транзакции в течение смены.' },
        { title: 'Сверка при закрытии', desc: 'Сравните ожидаемое с подсчитанным и запишите отклонение.' },
      ],
    },
    related: ['bookings', 'refunds'],
  },
};
